import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from '../../../utils/dayjsConfig';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  fetchPayrollExport,
  selectPayrollData,
  selectPayrollStatus,
  selectPayrollError,
  selectPayrollDateRange,
  clearPayroll,
} from '../../../redux/payroll/payrollSlice';
import { downloadPayrollCSV } from '../../../redux/payroll/payrollService';
import { selectCurrentVenue } from '../../../redux/slices/currentUserSlice';

import {
  PayrollReportContainer,
  PayrollReportContent,
  PayrollTableContainer,
  TitleReport,
  DatePickerRow,
} from './PayrollReport.styles';

const PayrollReport = () => {
  const dispatch = useDispatch();
  const venue = useSelector(selectCurrentVenue);
  const venueId = venue?.id;

  const payrollData = useSelector(selectPayrollData);
  const status = useSelector(selectPayrollStatus);
  const error = useSelector(selectPayrollError);
  const { startDate, endDate } = useSelector(selectPayrollDateRange);

  const [localStart, setLocalStart] = useState(dayjs().startOf('month'));
  const [localEnd, setLocalEnd] = useState(dayjs().endOf('month'));

  const isLoading = status === 'loading';
  const hasData = status === 'succeeded' && payrollData.length > 0;

  const handleGenerateReport = () => {
    if (!venueId) return;
    const start = localStart.format('YYYY-MM-DD');
    const end = localEnd.format('YYYY-MM-DD');
    dispatch(fetchPayrollExport({ venueId, startDate: start, endDate: end }));
  };

  const handleDownloadCSV = () => {
    if (!hasData) return;
    downloadPayrollCSV(payrollData, startDate, endDate);
  };

  const handleClear = () => {
    dispatch(clearPayroll());
  };

  return (
    <PayrollReportContainer>
      <PayrollReportContent>
        <PayrollTableContainer>
          <TitleReport variant="bodyRegular">Payroll Export</TitleReport>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePickerRow>
              <DatePicker
                label="Pay Period Start"
                value={localStart}
                onChange={(val) => setLocalStart(val)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="Pay Period End"
                value={localEnd}
                onChange={(val) => setLocalEnd(val)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <Button
                variant="contained"
                onClick={handleGenerateReport}
                disabled={isLoading || !venueId}
              >
                {isLoading ? 'Generating…' : 'Generate Report'}
              </Button>
              {(hasData || status === 'failed') && (
                <Button variant="outlined" color="secondary" onClick={handleClear}>
                  Clear
                </Button>
              )}
            </DatePickerRow>
          </LocalizationProvider>

          {/* Loading */}
          {isLoading && (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          )}

          {/* Error */}
          {status === 'failed' && error && (
            <Alert severity="error">{error}</Alert>
          )}

          {/* Empty result */}
          {status === 'succeeded' && payrollData.length === 0 && (
            <Alert severity="info">No payroll data found for the selected period.</Alert>
          )}

          {/* Results table */}
          {hasData && (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" px={1}>
                <Typography variant="body2" color="text.secondary">
                  {payrollData.length} employee{payrollData.length !== 1 ? 's' : ''} — {startDate} to {endDate}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleDownloadCSV}
                >
                  Download CSV
                </Button>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell><strong>Employee ID</strong></TableCell>
                    <TableCell align="right"><strong>Hours Worked</strong></TableCell>
                    <TableCell align="right"><strong>Tips Earned</strong></TableCell>
                    <TableCell align="right"><strong>Shifts</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payrollData.map((row) => (
                    <TableRow key={row.employeeDocId} hover>
                      <TableCell>{row.displayName}</TableCell>
                      <TableCell>{row.externalId || '—'}</TableCell>
                      <TableCell align="right">{row.regularHours.toFixed(2)}</TableCell>
                      <TableCell align="right">${row.tipsOwedDollars.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.totalShifts}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </PayrollTableContainer>
      </PayrollReportContent>
    </PayrollReportContainer>
  );
};

export default PayrollReport;
